import type { MarketCheckResponse } from '../types'

function fmtPct(p?: number | null): string {
  if (typeof p !== 'number' || Number.isNaN(p)) return ''
  return `${(p * 100).toFixed(0)}%`
}

export default function MarketCheckModal({
  open,
  onClose,
  data,
  error,
  onSave,
  saving,
  saved,
}: {
  open: boolean
  onClose: () => void
  data: MarketCheckResponse | null
  error?: string | null
  onSave?: () => void
  saving?: boolean
  saved?: boolean
}) {
  if (!open) return null

  const dt = data?.now ? new Date(data.now) : null
  const asOf = dt && !Number.isNaN(dt.getTime()) ? dt.toLocaleString() : 'now'

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(ev) => ev.stopPropagation()}>
        <div className="modal-header">
          <h2 className="text-lg sm:text-xl font-semibold text-ink">{`Prediction markets as of ${asOf}`}</h2>
          <button className="modal-close" type="button" onClick={onClose}>
            ×
          </button>
        </div>
        <div className="modal-body">
          <div className="flex items-center justify-end gap-2 mb-3">
            <button
              type="button"
              className="rounded-xl px-3 py-2 text-sm font-semibold border border-rule bg-paper shadow-delphi hover:bg-white/30 transition disabled:opacity-60"
              onClick={onSave}
              disabled={!onSave || saving || saved || !data}
              title="Save this market snapshot to the database so you can revisit it later."
            >
              {saved ? 'Saved' : saving ? 'Saving…' : 'Save snapshot'}
            </button>
          </div>

          {error && (
            <div className="rounded-delphi border border-wine/30 bg-wine/10 p-3 text-sm text-wine">{error}</div>
          )}

          {data && (
            <>
              <div className="rounded-delphi border border-rule bg-paper p-3">
                <div className="font-semibold text-ink">Summary</div>
                <div className="mt-2 text-sm text-ink/90 leading-relaxed">{data.headline_summary}</div>
              </div>

              <div className="mt-3 rounded-delphi border border-rule bg-paper p-3">
                <div className="font-semibold text-ink">Related markets</div>
                {data.markets?.length ? (
                  <div className="mt-2 space-y-2">
                    {data.markets.slice(0, 6).map((m, idx) => (
                      <div key={idx} className="rounded-delphi border border-rule bg-paper2 p-3">
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <div className="text-[11px] text-muted tracking-wider font-semibold">
                              {(m.platform || 'other').toString().toUpperCase()}
                            </div>
                            <a
                              className="mt-1 block text-sm font-semibold text-ink hover:underline"
                              href={m.url}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {m.title || m.url}
                            </a>
                          </div>
                          <div className="shrink-0 text-right">
                            <div className="text-xs text-muted">{m.price_text}</div>
                            {fmtPct(m.implied_probability) && (
                              <div className="mt-1 text-[11px] text-muted">Implied: {fmtPct(m.implied_probability)}</div>
                            )}
                          </div>
                        </div>
                        {m.notes ? <div className="mt-2 text-xs text-muted">{m.notes}</div> : null}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="mt-2 text-sm text-muted">
                    No close matches found on Metaculus, Polymarket, Kalshi, or OddsChecker.
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}


